import * as pc from 'playcanvas';

export interface RegionVector {
    x: number;
    y: number;
    z: number;
}
export interface RegionShape {
    shape: 'sphere' | 'box';
    position: RegionVector;
    rotation: RegionVector;
    /** Full dimensions; sphere with unequal dimensions is an ellipsoid. */
    size: RegionVector;
    /** Soft boundary width as a fraction of the half-size (0..1). */
    feather: number;
}
export const DEFAULT_REGION: RegionShape = {
    shape: 'sphere',
    position: { x: 0, y: 0, z: 0 },
    rotation: { x: 0, y: 0, z: 0 },
    size: { x: 6, y: 6, z: 6 },
    feather: 0.25,
};
export function finiteRegionNumber(value: unknown, fallback: number, min = -1e6, max = 1e6): number {
    return typeof value === 'number' && Number.isFinite(value) ? Math.min(max, Math.max(min, value)) : fallback;
}
export function normalizeRegion(value: Partial<RegionShape> | undefined): RegionShape {
    const vector = (v: RegionVector | undefined, fallback: RegionVector, min = -1e6): RegionVector => ({
        x: finiteRegionNumber(v?.x, fallback.x, min),
        y: finiteRegionNumber(v?.y, fallback.y, min),
        z: finiteRegionNumber(v?.z, fallback.z, min),
    });
    return {
        shape: value?.shape === 'box' ? 'box' : 'sphere',
        position: vector(value?.position, DEFAULT_REGION.position),
        rotation: vector(value?.rotation, DEFAULT_REGION.rotation),
        size: vector(value?.size, DEFAULT_REGION.size, 0.01),
        feather: finiteRegionNumber(value?.feather, 0.25, 0, 1),
    };
}
/** The editor's scene coordinates reflect Z relative to the PlayCanvas runtime. */
export function regionMatrix(shape: RegionShape): pc.Mat4 {
    const q = new pc.Quat().setFromEulerAngles(-shape.rotation.x, -shape.rotation.y, shape.rotation.z);
    return new pc.Mat4().setTRS(
        new pc.Vec3(shape.position.x, shape.position.y, -shape.position.z),
        q,
        new pc.Vec3(shape.size.x / 2, shape.size.y / 2, shape.size.z / 2),
    );
}
export function regionWeight(shape: RegionShape, scenePoint: RegionVector): number {
    const p = regionMatrix(shape)
        .invert()
        .transformPoint(new pc.Vec3(scenePoint.x, scenePoint.y, -scenePoint.z));
    const d = shape.shape === 'box' ? Math.max(Math.abs(p.x), Math.abs(p.y), Math.abs(p.z)) : p.length();
    if (shape.feather === 0) return d <= 1 ? 1 : 0;
    const t = Math.min(1, Math.max(0, (d - (1 - shape.feather)) / shape.feather));
    return 1 - t * t * (3 - 2 * t);
}
export const shaderNumber = (v: number): string => (Number.isInteger(v) ? `${v}.0` : v.toPrecision(9));
export function regionShader(shape: RegionShape, name: string, lang: 'glsl' | 'wgsl'): string {
    const m = Array.from(regionMatrix(shape).invert().data).map(shaderNumber).join(', ');
    const v3 = lang === 'glsl' ? 'vec3' : 'vec3f';
    const v4 = lang === 'glsl' ? 'vec4' : 'vec4f';
    const matrix = lang === 'glsl' ? 'mat4' : 'mat4x4f';
    const d = shape.shape === 'box' ? 'max(max(abs(p.x), abs(p.y)), abs(p.z))' : 'length(p)';
    const result =
        shape.feather === 0
            ? lang === 'glsl'
                ? '(d <= 1.0 ? 1.0 : 0.0)'
                : 'select(0.0, 1.0, d <= 1.0)'
            : `1.0 - smoothstep(${shaderNumber(1 - shape.feather)}, 1.0, d)`;
    return lang === 'glsl'
        ? `float ${name}(vec3 center) { vec3 p = (${matrix}(${m}) * ${v4}(center, 1.0)).xyz; float d = ${d}; return ${result}; }\n`
        : `fn ${name}(center: ${v3}) -> f32 { let p = (${matrix}(${m}) * ${v4}(center, 1.0)).xyz; let d = ${d}; return ${result}; }\n`;
}
/** Per-region uniform storage: the inverse region matrix and params (x = feather). */
export interface RegionUniform {
    matrix: Float32Array;
    params: Float32Array;
}
export const createRegionUniforms = (count: number): RegionUniform[] =>
    Array.from({ length: count }, () => ({ matrix: new Float32Array(16), params: new Float32Array(4) }));
/** Write a region's transform and feather into its uniforms (moving it never recompiles). */
export function writeRegionUniform(target: RegionUniform, shape: RegionShape): void {
    target.matrix.set(regionMatrix(shape).invert().data);
    target.params[0] = shape.feather;
}
/**
 * Like regionShader, but the transform and feather come from `uniform` (mat4) and
 * `${uniform}P` (vec4, x = feather), so they can change without recompiling. Only the shape
 * type (sphere / box) is baked in. A feather of 0 gives the same hard edge.
 */
export function regionUniformShader(shape: RegionShape['shape'], name: string, uniform: string, lang: 'glsl' | 'wgsl'): string {
    const d = shape === 'box' ? 'max(max(abs(p.x), abs(p.y)), abs(p.z))' : 'length(p)';
    const u = lang === 'glsl' ? uniform : `uniform.${uniform}`;
    const weight = `clamp((d - (1.0 - ${u}P.x)) / max(${u}P.x, 0.00001), 0.0, 1.0)`;
    return lang === 'glsl'
        ? `uniform mat4 ${uniform};\nuniform vec4 ${uniform}P;\nfloat ${name}(vec3 center) { vec3 p = (${uniform} * vec4(center, 1.0)).xyz; float d = ${d}; float t = ${weight}; return 1.0 - t * t * (3.0 - 2.0 * t); }\n`
        : `uniform ${uniform}: mat4x4f;\nuniform ${uniform}P: vec4f;\nfn ${name}(center: vec3f) -> f32 { let p = (${u} * vec4f(center, 1.0)).xyz; let d = ${d}; let t = ${weight}; return 1.0 - t * t * (3.0 - 2.0 * t); }\n`;
}
export function regionColor(value: unknown, fallback: string): string {
    return typeof value === 'string' && /^#[0-9a-f]{6}$/i.test(value) ? value : fallback;
}
export function colorShader(hex: string, lang: 'glsl' | 'wgsl'): string {
    const channels = [1, 3, 5].map((i) => shaderNumber(parseInt(hex.slice(i, i + 2), 16) / 255));
    return `${lang === 'glsl' ? 'vec3' : 'vec3f'}(${channels.join(', ')})`;
}

/** Position, orientation and half-extents of a region on an entity parented to the scene root. */
export function applyRegionToEntity(entity: pc.Entity, shape: RegionShape): void {
    entity.setLocalPosition(shape.position.x, shape.position.y, -shape.position.z);
    entity.setLocalEulerAngles(-shape.rotation.x, -shape.rotation.y, shape.rotation.z);
    entity.setLocalScale(
        Math.max(0.005, shape.size.x / 2),
        Math.max(0.005, shape.size.y / 2),
        Math.max(0.005, shape.size.z / 2),
    );
}
/** Inverse of applyRegionToEntity: read a gizmo-edited entity back into editor scene coordinates. */
export function regionFromEntity(entity: pc.Entity): Pick<RegionShape, 'position' | 'rotation' | 'size'> {
    const p = entity.getLocalPosition();
    const e = entity.getLocalEulerAngles();
    const s = entity.getLocalScale();
    const round = (v: number) => Math.round(v * 1000) / 1000;
    return {
        position: { x: round(p.x), y: round(p.y), z: round(-p.z) },
        rotation: { x: round(-e.x), y: round(-e.y), z: round(e.z) },
        size: { x: round(Math.max(0.01, s.x * 2)), y: round(Math.max(0.01, s.y * 2)), z: round(Math.max(0.01, s.z * 2)) },
    };
}
/** Draw a unit sphere or box wireframe through `matrix` (editor visualisation only). */
export function drawRegionWireframe(app: pc.Application, matrix: pc.Mat4, shape: RegionShape['shape'], color: pc.Color): void {
    const line = (a: pc.Vec3, b: pc.Vec3) => app.drawLine(matrix.transformPoint(a), matrix.transformPoint(b), color, false);
    if (shape === 'sphere') {
        for (let axis = 0; axis < 3; axis++)
            for (let i = 0; i < 48; i++) {
                const point = (t: number) => {
                    const c = Math.cos(t),
                        s = Math.sin(t);
                    return axis === 0 ? new pc.Vec3(0, c, s) : axis === 1 ? new pc.Vec3(c, 0, s) : new pc.Vec3(c, s, 0);
                };
                line(point((i * Math.PI) / 24), point(((i + 1) * Math.PI) / 24));
            }
    } else {
        const points = Array.from({ length: 8 }, (_, i) => new pc.Vec3(i & 1 ? 1 : -1, i & 2 ? 1 : -1, i & 4 ? 1 : -1));
        for (let i = 0; i < 8; i++)
            for (const bit of [1, 2, 4]) if (!(i & bit)) line(points[i].clone(), points[i | bit].clone());
    }
}
